/**
 * News Article Page
 * In-app news reader with preview + link to full article on source site
 *
 * @package ChainVault
 * @since   1.0.0
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import './VaultMarketPage.css';
import './NewsArticlePage.css';

function timeAgo( timestamp ) {
    if ( !timestamp ) return '';
    const date = typeof timestamp === 'number' ? new Date( timestamp * 1000 ) : new Date( timestamp );
    const now = new Date();
    const diff = Math.floor( ( now - date ) / 1000 );
    if ( diff < 60 ) return 'just now';
    if ( diff < 3600 ) return Math.floor( diff / 60 ) + 'm ago';
    if ( diff < 86400 ) return Math.floor( diff / 3600 ) + 'h ago';
    if ( diff < 2592000 ) return Math.floor( diff / 86400 ) + 'd ago';
    return date.toLocaleDateString();
}

const POS_KEYWORDS = [ 'surge', 'rally', 'bullish', 'gain', 'rise', 'pump', 'high', 'record', 'positive', 'growth', 'soar', 'jump', 'boom', 'breakthrough', 'adoption', 'partnership' ];
const NEG_KEYWORDS = [ 'crash', 'dump', 'bearish', 'fall', 'drop', 'loss', 'low', 'negative', 'decline', 'hack', 'plunge', 'collapse', 'concerns', 'warning', 'risk', 'exploit', 'breach' ];

function analyzeSentiment( article ) {
    const text = ( ( article.title || '' ) + ' ' + ( article.body || '' ) ).toLowerCase();
    const posMatches = POS_KEYWORDS.filter( w => text.includes( w ) );
    const negMatches = NEG_KEYWORDS.filter( w => text.includes( w ) );
    let label = 'NEUTRAL';
    if ( posMatches.length > negMatches.length ) label = 'POSITIVE';
    else if ( negMatches.length > posMatches.length ) label = 'NEGATIVE';
    return { label, posCount: posMatches.length, negCount: negMatches.length, posMatches, negMatches };
}

function getSentiment( article ) {
    return analyzeSentiment( article ).label;
}

const COIN_MAP = {
    'bitcoin': { id: 'bitcoin', symbol: 'BTC', name: 'Bitcoin' },
    ' btc': { id: 'bitcoin', symbol: 'BTC', name: 'Bitcoin' },
    'ethereum': { id: 'ethereum', symbol: 'ETH', name: 'Ethereum' },
    ' eth ': { id: 'ethereum', symbol: 'ETH', name: 'Ethereum' },
    'solana': { id: 'solana', symbol: 'SOL', name: 'Solana' },
    ' sol ': { id: 'solana', symbol: 'SOL', name: 'Solana' },
    'cardano': { id: 'cardano', symbol: 'ADA', name: 'Cardano' },
    ' ada ': { id: 'cardano', symbol: 'ADA', name: 'Cardano' },
    'ripple': { id: 'ripple', symbol: 'XRP', name: 'XRP' },
    ' xrp ': { id: 'ripple', symbol: 'XRP', name: 'XRP' },
    'dogecoin': { id: 'dogecoin', symbol: 'DOGE', name: 'Dogecoin' },
    ' doge ': { id: 'dogecoin', symbol: 'DOGE', name: 'Dogecoin' },
    'polkadot': { id: 'polkadot', symbol: 'DOT', name: 'Polkadot' },
    'chainlink': { id: 'chainlink', symbol: 'LINK', name: 'Chainlink' },
    'avalanche': { id: 'avalanche-2', symbol: 'AVAX', name: 'Avalanche' },
    'tron': { id: 'tron', symbol: 'TRX', name: 'Tron' },
    ' bnb ': { id: 'binancecoin', symbol: 'BNB', name: 'BNB' },
    'binance coin': { id: 'binancecoin', symbol: 'BNB', name: 'BNB' },
    'tether': { id: 'tether', symbol: 'USDT', name: 'Tether' },
    ' usdt': { id: 'tether', symbol: 'USDT', name: 'Tether' },
    ' usdc': { id: 'usd-coin', symbol: 'USDC', name: 'USDC' },
    'shiba inu': { id: 'shiba-inu', symbol: 'SHIB', name: 'Shiba Inu' },
    'litecoin': { id: 'litecoin', symbol: 'LTC', name: 'Litecoin' },
    'polygon': { id: 'matic-network', symbol: 'MATIC', name: 'Polygon' },
};

function detectCoins( article ) {
    const text = ( ' ' + ( article.title || '' ) + ' ' + ( article.body || '' ) + ' ' ).toLowerCase();
    const found = {};
    for ( const [ keyword, coin ] of Object.entries( COIN_MAP ) ) {
        if ( text.includes( keyword ) ) { found[ coin.id ] = coin; }
    }
    return Object.values( found ).slice( 0, 3 );
}

function calcReadingTime( body ) {
    const words = ( body || '' ).split( /\s+/ ).length;
    const minutes = Math.max( 1, Math.ceil( words / 200 ) );
    return minutes + ' min read';
}

function MiniSparkline( { data, positive } ) {
    if ( !data || data.length < 2 ) return null;
    const w = 100;
    const h = 30;
    const min = Math.min( ...data );
    const max = Math.max( ...data );
    const range = max - min || 1;
    const points = data.map( ( v, i ) => {
        const x = ( i / ( data.length - 1 ) ) * w;
        const y = h - ( ( v - min ) / range ) * ( h - 4 ) - 2;
        return x + ',' + y;
    } ).join( ' ' );
    const color = positive ? '#16a34a' : '#dc2626';
    return React.createElement( 'svg', { width: w, height: h, viewBox: '0 0 ' + w + ' ' + h, style: { display: 'block' } },
        React.createElement( 'polyline', { points, fill: 'none', stroke: color, strokeWidth: '1.5' } )
    );
}
function NewsArticlePage() {
    const { articleId } = useParams();
    const navigate = useNavigate();
    const [ article, setArticle ] = useState( null );
    const [ relatedArticles, setRelatedArticles ] = useState( [] );
    const [ mentionedCoins, setMentionedCoins ] = useState( [] );
    const [ trendingCoins, setTrendingCoins ] = useState( [] );
    const [ loading, setLoading ] = useState( true );
    const [ error, setError ] = useState( null );

    const fetchArticle = useCallback( async () => {
        try {
            setLoading( true );
            setError( null );

            // Load from sessionStorage (set when user clicked the news card)
            const cached = sessionStorage.getItem( 'cv_news_article_' + articleId );
            if ( cached ) {
                setArticle( JSON.parse( cached ) );
            } else {
                setError( 'Article not found. Please go back to the news list.' );
            }

            // Fetch related news from RSS to populate "More News" section
            const rssUrl = 'https://api.rss2json.com/v1/api.json?rss_url=https://cointelegraph.com/rss&count=4';
            const res = await fetch( rssUrl );
            if ( res.ok ) {
                const json = await res.json();
                const related = ( json.items || [] ).slice( 0, 4 ).map( ( item, idx ) => Object.assign( {}, item, {
                    _id: encodeURIComponent( ( item.title || '' ).slice( 0, 50 ).replace( /[^a-zA-Z0-9]/g, '-' ).toLowerCase() ) || ( 'related-' + idx ),
                    body: item.description || item.content || '',
                    imageurl: item.thumbnail || item.enclosure?.link,
                    source: item.author || 'news source',
                    source_info: { name: item.author || 'news source' },
                    published_on: item.pubDate ? Math.floor( new Date( item.pubDate ).getTime() / 1000 ) : Math.floor( Date.now() / 1000 ),
                    url: item.link,
                } ) );
                setRelatedArticles( related );
            }
        } catch ( err ) {
            console.error( err );
            if ( !sessionStorage.getItem( 'cv_news_article_' + articleId ) ) {
                setError( 'Could not load article' );
            }
        } finally {
            setLoading( false );
        }
    }, [ articleId ] );

    useEffect( () => { fetchArticle(); window.scrollTo( 0, 0 ); }, [ fetchArticle ] );

    /* Fetch live data for mentioned coins */
    useEffect( () => {
        if ( !article ) return;
        const coins = detectCoins( article );
        if ( coins.length === 0 ) return;
        const ids = coins.map( c => c.id ).join( ',' );
        fetch( 'https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&ids=' + ids + '&sparkline=true&price_change_percentage=24h' )
            .then( r => r.ok ? r.json() : [] )
            .then( data => setMentionedCoins( data || [] ) )
            .catch( () => {} );
    }, [ article ] );

    /* Fetch top 5 trending coins */
    useEffect( () => {
        fetch( 'https://api.coingecko.com/api/v3/search/trending' )
            .then( r => r.ok ? r.json() : { coins: [] } )
            .then( data => setTrendingCoins( ( data.coins || [] ).slice( 0, 5 ).map( c => c.item ) ) )
            .catch( () => {} );
    }, [] );

    if ( loading && !article ) {
        return React.createElement( 'div', { className: 'cv-page na-page vm-wrap', 'data-theme': 'dark' },
            React.createElement( 'div', { className: 'na-skeleton-back' } ),
            React.createElement( 'div', { className: 'na-skeleton-article' },
                React.createElement( 'div', { className: 'na-skeleton-hero shimmer' } ),
                React.createElement( 'div', { className: 'na-skeleton-meta shimmer' } ),
                React.createElement( 'div', { className: 'na-skeleton-title shimmer' } ),
                React.createElement( 'div', { className: 'na-skeleton-title2 shimmer' } ),
                React.createElement( 'div', { className: 'na-skeleton-text shimmer' } ),
                React.createElement( 'div', { className: 'na-skeleton-text shimmer' } ),
                React.createElement( 'div', { className: 'na-skeleton-text short shimmer' } )
            )
        );
    }

    if ( error || !article ) {
        return React.createElement( 'div', { className: 'cv-page na-page vm-wrap', 'data-theme': 'dark' },
            React.createElement( 'div', { className: 'na-error' },
                React.createElement( 'p', null, error || 'Article not found' ),
                React.createElement( Link, { to: '/market', className: 'na-back' }, '\u2190 Back to Market' )
            )
        );
    }

    const sentimentData = analyzeSentiment( article );
    const sentiment = sentimentData.label;
    const sentClass = sentiment === 'POSITIVE' ? 'positive' : sentiment === 'NEGATIVE' ? 'negative' : 'neutral';
    const categories = article.categories ? article.categories.split( '|' ).slice( 0, 5 ) : [];

    return React.createElement( 'div', { className: 'cv-page na-page vm-wrap', 'data-theme': 'dark' },

        /* Back link */
        React.createElement( Link, { to: '/market', className: 'na-back-link' }, '\u2190 Back to Market' ),

        /* Article */
        React.createElement( 'article', { className: 'na-article' },
            /* Hero image */
            article.imageurl && React.createElement( 'div', { className: 'na-hero' },
                React.createElement( 'img', {
                    src: article.imageurl,
                    alt: article.title,
                    onError: e => { e.target.style.display = 'none'; }
                } ),
                React.createElement( 'span', { className: 'na-sent-badge ' + sentClass }, sentiment )
            ),

            /* Meta */
            React.createElement( 'div', { className: 'na-meta' },
                React.createElement( 'span', { className: 'na-source' }, article.source_info?.name || article.source ),
                React.createElement( 'span', { className: 'na-dot' }, '\u2022' ),
                React.createElement( 'span', { className: 'na-time' }, timeAgo( article.published_on ) ),
                React.createElement( 'span', { className: 'na-dot' }, '\u2022' ),
                React.createElement( 'span', { className: 'na-reading-time' }, '\u{1F4D6} ' + calcReadingTime( article.body || '' ) )
            ),

            /* Title */
            React.createElement( 'h1', { className: 'na-title' }, article.title ),

            /* Categories */
            categories.length > 0 && React.createElement( 'div', { className: 'na-categories' },
                categories.map( ( cat, i ) =>
                    React.createElement( 'span', { key: i, className: 'na-category' }, cat.trim() )
                )
            ),

            /* Body — full preview from API */
            React.createElement( 'div', { className: 'na-body' },
                ( article.body || 'No preview available for this article.' ).split( '\n' ).filter( p => p.trim() ).map( ( p, i ) =>
                    React.createElement( 'p', { key: i }, p )
                )
            ),

            /* Read full article CTA */
            React.createElement( 'div', { className: 'na-cta-card' },
                React.createElement( 'div', { className: 'na-cta-text' },
                    React.createElement( 'h3', null, 'Continue Reading' ),
                    React.createElement( 'p', null, 'This is a preview. Read the full article on ' + ( article.source_info?.name || article.source ) + ' for complete details.' )
                ),
                React.createElement( 'a', {
                    href: article.url,
                    target: '_blank',
                    rel: 'noopener noreferrer',
                    className: 'na-cta-btn',
                }, 'Read Full Article \u2192' )
            )
        ),

                /* Mentioned Coins Card */
        mentionedCoins.length > 0 && React.createElement( 'div', { className: 'na-section na-coins-section na-animate-in' },
            React.createElement( 'h2', { className: 'na-section-title' }, '\u{1F4B0} Coins Mentioned' ),
            React.createElement( 'div', { className: 'na-coins-grid' },
                mentionedCoins.map( ( coin, i ) => {
                    const positive = ( coin.price_change_percentage_24h || 0 ) >= 0;
                    return React.createElement( 'div', {
                        key: i,
                        className: 'na-coin-card',
                        onClick: () => navigate( '/market/coin/' + coin.id ),
                    },
                        React.createElement( 'div', { className: 'na-coin-card-header' },
                            React.createElement( 'img', { src: coin.image, alt: coin.name, className: 'na-coin-card-img' } ),
                            React.createElement( 'div', null,
                                React.createElement( 'div', { className: 'na-coin-card-name' }, coin.name ),
                                React.createElement( 'div', { className: 'na-coin-card-symbol' }, ( coin.symbol || '' ).toUpperCase() )
                            )
                        ),
                        React.createElement( 'div', { className: 'na-coin-card-price-row' },
                            React.createElement( 'div', null,
                                React.createElement( 'div', { className: 'na-coin-card-price' }, '\u0024' + ( coin.current_price || 0 ).toLocaleString( 'en-US', { maximumFractionDigits: 4 } ) ),
                                React.createElement( 'div', { className: 'na-coin-card-chg ' + ( positive ? 'positive' : 'negative' ) },
                                    ( positive ? '\u2191 ' : '\u2193 ' ) + Math.abs( coin.price_change_percentage_24h || 0 ).toFixed( 2 ) + '%'
                                )
                            ),
                            coin.sparkline_in_7d && coin.sparkline_in_7d.price && React.createElement( 'div', { className: 'na-coin-card-spark' },
                                React.createElement( MiniSparkline, { data: coin.sparkline_in_7d.price.slice( -24 ), positive } )
                            )
                        )
                    );
                } )
            )
        ),

        /* Sentiment Analysis Card */
        React.createElement( 'div', { className: 'na-section na-sentiment-section na-animate-in' },
            React.createElement( 'h2', { className: 'na-section-title' }, '\u{1F4CA} Sentiment Analysis' ),
            React.createElement( 'div', { className: 'na-sent-card' },
                React.createElement( 'div', { className: 'na-sent-label-row' },
                    React.createElement( 'div', { className: 'na-sent-label-text' }, 'Overall Sentiment'),
                    React.createElement( 'div', { className: 'na-sent-badge-big ' + sentClass }, sentiment )
                ),
                React.createElement( 'div', { className: 'na-sent-bars' },
                    React.createElement( 'div', { className: 'na-sent-bar-row' },
                        React.createElement( 'span', { className: 'na-sent-bar-label positive' }, 'Positive Signals' ),
                        React.createElement( 'div', { className: 'na-sent-bar' },
                            React.createElement( 'div', { className: 'na-sent-bar-fill positive', style: { width: Math.min( 100, sentimentData.posCount * 20 ) + '%' } } )
                        ),
                        React.createElement( 'span', { className: 'na-sent-bar-count' }, sentimentData.posCount )
                    ),
                    React.createElement( 'div', { className: 'na-sent-bar-row' },
                        React.createElement( 'span', { className: 'na-sent-bar-label negative' }, 'Negative Signals' ),
                        React.createElement( 'div', { className: 'na-sent-bar' },
                            React.createElement( 'div', { className: 'na-sent-bar-fill negative', style: { width: Math.min( 100, sentimentData.negCount * 20 ) + '%' } } )
                        ),
                        React.createElement( 'span', { className: 'na-sent-bar-count' }, sentimentData.negCount )
                    )
                )
            )
        ),

        /* Trending Coins */
        trendingCoins.length > 0 && React.createElement( 'div', { className: 'na-section na-animate-in' },
            React.createElement( 'h2', { className: 'na-section-title' }, '\u{1F525} Trending Right Now' ),
            React.createElement( 'div', { className: 'na-trending-grid' },
                trendingCoins.map( ( coin, i ) =>
                    React.createElement( 'div', {
                        key: i,
                        className: 'na-trending-card',
                        onClick: () => navigate( '/market/coin/' + coin.id ),
                    },
                        React.createElement( 'span', { className: 'na-trending-rank' }, '#' + ( i + 1 ) ),
                        React.createElement( 'img', { src: coin.small, alt: coin.name, className: 'na-trending-img' } ),
                        React.createElement( 'div', null,
                            React.createElement( 'div', { className: 'na-trending-name' }, coin.name ),
                            React.createElement( 'div', { className: 'na-trending-symbol' }, ( coin.symbol || '' ).toUpperCase() )
                        )
                    )
                )
            )
        ),

        /* Related News */
        relatedArticles.length > 0 && React.createElement( 'div', { className: 'na-related na-animate-in' },
            React.createElement( 'h2', { className: 'na-related-title' }, 'More News' ),
            React.createElement( 'div', { className: 'na-related-grid' },
                relatedArticles.map( ( art, i ) => {
                    const sent = getSentiment( art );
                    const sentCls = sent === 'POSITIVE' ? 'positive' : sent === 'NEGATIVE' ? 'negative' : 'neutral';
                    return React.createElement( 'div', {
                        key: i,
                        className: 'na-related-card',
                        onClick: () => {
                            sessionStorage.setItem( 'cv_news_article_' + ( art._id || art.id ), JSON.stringify( art ) );
                            navigate( '/market/news/' + ( art._id || art.id ) );
                        },
                    },
                        art.imageurl && React.createElement( 'img', { src: art.imageurl, alt: art.title, className: 'na-related-img', onError: e => { e.target.style.display = 'none'; } } ),
                        React.createElement( 'div', { className: 'na-related-body' },
                            React.createElement( 'div', { className: 'na-related-meta' },
                                React.createElement( 'span', { className: 'na-related-source' }, art.source_info?.name || art.source ),
                                React.createElement( 'span', null, ' \u2022 ' + timeAgo( art.published_on ) )
                            ),
                            React.createElement( 'h3', { className: 'na-related-headline' }, art.title ),
                            React.createElement( 'span', { className: 'na-related-sent ' + sentCls }, sent )
                        )
                    );
                } )
            )
        )
    );
}

export default NewsArticlePage;
